import React, { useState, useMemo } from "react";
import { Search, ChevronLeft, ChevronRight, CheckCircle, XCircle, Pencil, Check, X, Download, CalendarDays } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BBQBookingsTable - Paginated table for BBQ bookings with search
 */
const BBQBookingsTable = ({ 
  bookings, 
  onVerifyPayment,
  onCancel,
  onChangeDate,
  pageSize = 15
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingTxnBookingId, setEditingTxnBookingId] = useState(null);
  const [editingTxnValue, setEditingTxnValue] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  const handleSaveBbqTxn = async (booking) => {
    try {
      await axios.put(`${API}/admin/bbq/bookings/${booking.booking_id}/update-transaction?onepa_transaction_number=${encodeURIComponent(editingTxnValue)}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      booking.onepa_transaction_number = editingTxnValue;
      toast.success("Transaction reference updated");
      setEditingTxnBookingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };
  
  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    let filtered = bookings;
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.name?.toLowerCase().includes(query) ||
        b.booking_code?.toLowerCase().includes(query) ||
        b.block_unit?.toLowerCase().includes(query) ||
        b.mobile?.includes(query) ||
        b.email?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date_asc': return (a.date || '').localeCompare(b.date || '');
        case 'date_desc': return (b.date || '').localeCompare(a.date || '');
        case 'code_asc': return (a.booking_code || '').localeCompare(b.booking_code || '');
        case 'code_desc': return (b.booking_code || '').localeCompare(a.booking_code || '');
        case 'name_asc': return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        default: return 0;
      }
    });
    
    return filtered;
  }, [bookings, searchQuery, statusFilter, sortBy]);
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedBookings = filteredBookings.slice(startIndex, startIndex + pageSize);
  
  // Reset to page 1 when search changes
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-gray-200 text-gray-700',
      completed_unpaid: 'bg-orange-200 text-orange-800 border border-orange-400',
      ongoing: 'bg-blue-100 text-blue-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    if (status === 'completed_unpaid') return 'COMPLETED - UNPAID';
    return status?.toUpperCase();
  };

  const getPitLabel = (pit) => {
    if (pit === 'both') return 'Both';
    return `Pit ${pit?.slice(-1)}`;
  };

  const getSlotTime = (slot) => {
    return slot === 'afternoon' ? '1PM' : '6PM';
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, code, unit, mobile..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-2 border-black"
            data-testid="bbq-search-input"
          />
        </div>
      </div>

      {/* Status Filter & Sort */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex gap-1 flex-wrap">
          {['all', 'pending', 'paid', 'completed', 'completed_unpaid', 'ongoing', 'cancelled'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
              className={`px-2.5 py-1 text-xs font-bold border-2 transition-colors ${
                statusFilter === s
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`bbq-status-filter-${s}`}
            >
              {s === 'all' ? 'All' : s === 'completed_unpaid' ? 'Unpaid' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
          className="px-2 py-1 text-xs border-2 border-gray-300 font-bold bg-white"
          data-testid="bbq-sort-select"
        >
          <option value="date_desc">Date (Newest)</option>
          <option value="date_asc">Date (Oldest)</option>
          <option value="code_desc">Code (Z-A)</option>
          <option value="code_asc">Code (A-Z)</option>
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
        </select>
        <div className="text-sm text-gray-600 ml-auto">
          Showing {filteredBookings.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + pageSize, filteredBookings.length)} of {filteredBookings.length} bookings
        </div>
      </div>
      
      {/* Table */}
      {filteredBookings.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No BBQ bookings found</p>
      ) : (
        <div className="overflow-x-auto border-2 border-black">
          <table className="w-full text-sm">
            <thead className="bg-orange-100">
              <tr>
                <th className="p-3 text-left text-sm font-bold">Code</th>
                <th className="p-3 text-left text-sm font-bold">Resident</th>
                <th className="p-3 text-left text-sm font-bold">Date</th>
                <th className="p-3 text-left text-sm font-bold">Pit(s)</th>
                <th className="p-3 text-left text-sm font-bold">Status</th>
                <th className="p-3 text-left text-sm font-bold">Bank Ref#</th>
                <th className="p-3 text-left text-sm font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.map(booking => (
                <tr key={booking.booking_id} className="border-b hover:bg-orange-50">
                  <td className="p-3 font-mono font-bold text-orange-600 notranslate" translate="no">
                    {booking.booking_code}
                  </td>
                  <td className="p-3">
                    <div className="font-bold notranslate" translate="no">{booking.name}</div>
                    <div className="text-xs text-gray-500 notranslate" translate="no">{booking.block_unit}</div>
                  </td>
                  <td className="p-3 font-mono text-sm">
                    {formatDate(booking.date)} {getSlotTime(booking.slot_type)}
                  </td>
                  <td className="p-3">
                    {getPitLabel(booking.pit_selection)} (${booking.price})
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusBadge(booking.status)}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td className="p-3">
                    {editingTxnBookingId === booking.booking_id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editingTxnValue}
                          onChange={(e) => setEditingTxnValue(e.target.value)}
                          className="w-28 px-2 py-1 text-xs border-2 border-orange-400 font-mono"
                          autoFocus
                          data-testid={`bbq-txn-edit-input-${booking.booking_id}`}
                        />
                        <button onClick={() => handleSaveBbqTxn(booking)} className="p-1 bg-green-500 text-white rounded hover:bg-green-600" title="Save" data-testid={`bbq-txn-save-btn-${booking.booking_id}`}>
                          <Check size={12} />
                        </button>
                        <button onClick={() => setEditingTxnBookingId(null)} className="p-1 bg-gray-400 text-white rounded hover:bg-gray-500" title="Cancel">
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        {(booking.onepa_transaction_number || booking.bank_transfer_reference) ? (
                          <span className="font-mono text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-300">
                            {booking.onepa_transaction_number || booking.bank_transfer_reference}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                        {(booking.status === 'paid' || booking.status === 'completed' || booking.status === 'completed_unpaid') && (
                          <button
                            onClick={() => { setEditingTxnBookingId(booking.booking_id); setEditingTxnValue(booking.onepa_transaction_number || booking.bank_transfer_reference || ''); }}
                            className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                            title="Edit onePA Transaction Number"
                            data-testid={`bbq-txn-edit-btn-${booking.booking_id}`}
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {/* Verify Payment: pending or completed_unpaid */}
                      {(booking.status === 'pending' || booking.status === 'completed_unpaid') && onVerifyPayment && (
                        <button
                          onClick={() => onVerifyPayment(booking.booking_id)}
                          className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                          title="Verify Payment"
                          data-testid={`bbq-verify-${booking.booking_id}`}
                        >
                          <CheckCircle size={14} />
                        </button>
                      )}
                      {/* Change Date: pending or completed_unpaid */}
                      {(booking.status === 'pending' || booking.status === 'completed_unpaid') && onChangeDate && (
                        <button
                          onClick={() => onChangeDate(booking)}
                          className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                          title="Change Date"
                          data-testid={`bbq-change-date-${booking.booking_id}`}
                        >
                          <CalendarDays size={14} />
                        </button>
                      )}
                      {/* Cancel: pending or paid */}
                      {(booking.status === 'pending' || booking.status === 'paid') && onCancel && (
                        <button
                          onClick={() => onCancel(booking.booking_id, booking.booking_code)}
                          className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                          title="Cancel"
                        >
                          <XCircle size={14} />
                        </button>
                      )}
                      {/* Download Receipt: always available */}
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/admin/bbq/bookings/${booking.booking_id}/download-receipt`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `BBQ_Receipt_${booking.booking_code}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error("Failed to download receipt"); }
                        }}
                        className="p-1.5 bg-slate-600 text-white rounded hover:bg-slate-700"
                        title="Download Receipt"
                        data-testid={`bbq-download-receipt-${booking.booking_code}`}
                      >
                        <Download size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`p-2 border-2 border-black ${currentPage === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
          >
            <ChevronLeft size={18} />
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-10 h-10 border-2 border-black font-bold ${
                    currentPage === pageNum ? 'bg-orange-500 text-white' : 'bg-white hover:bg-orange-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`p-2 border-2 border-black ${currentPage === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default BBQBookingsTable;
